import Odometer from 'react-odometerjs';
import 'odometer/themes/odometer-theme-minimal.css';
import AnimatedLink from '../common/AnimatedLink';
import { base64SvgToSrc } from '../common/utils';
import {
    getCombinedMinutesSaved,
    isTaskAuthenticated,
    uniqueConnectorIdsFromActions,
} from './utils';
import TextWithArrows from '../common/TextWithArrows';
import { translations } from '../translations/translations';
import Toggle from './Toggle';
import TaskNotificationBar from './TaskNotificationBar';
import { useApp } from '../common/AppProvider';
import { AutomationTask, AutomationTaskStatus } from './types';
import { ExIcon, ExButton } from '@boomi/exosphere';

interface Props {
    task: AutomationTask;
    setDeletingTask: (taskId: string) => void;
}

const TaskItem = ({ task, setDeletingTask }: Props) => {
    const {
        connectors,
        language,
        isOnline,
        getActivations,
        filteredTasks,
        alertError,
        syncAndUpdate,
    } = useApp();

    const handleToggle = async (taskId: string) => {
        const task = filteredTasks.find((t) => t.id === taskId);

        if (!task) {
            alertError({
                message: translations[language].ERROR_unknown,
            });
            return;
        }

        const response = await syncAndUpdate([
            {
                ...task,
                isActive: !task.isActive,
                status: AutomationTaskStatus.Modified,
                updatedAt: new Date().toISOString(),
            },
        ]);

        if (response && response.taskErrors.length > 0) {
            alertError({
                message: translations[language].TASK_enable_failed,
            });
        }
    };

    // Get the task creation date, so it can be displayed. Since the initial tasks made way back
    // don't have a proper creation date, when displaying them we'll use the updatedAt or a default
    // fallback date instead.
    const getCreationDate = (task: AutomationTask) => {
        const createdDate = new Date(task.createdAt as string);
        const updatedDate = new Date(task.updatedAt as string);
        const fallbackDate = new Date('2024-02-01T12:00:00'); // roughly when TA went EA

        return createdDate.getFullYear() >= fallbackDate.getFullYear()
            ? createdDate.toLocaleDateString(navigator.language)
            : updatedDate.getFullYear() >= fallbackDate.getFullYear()
              ? updatedDate.toLocaleDateString(navigator.language)
              : fallbackDate.toLocaleDateString(navigator.language);
    };

    return (
        <div className="task">
            <AnimatedLink className="task-layout ta-tile ta-tile-interactive" to={task.id}>
                <div className="task-icons">
                    <img
                        src={base64SvgToSrc(connectors[task.trigger!.connectorId].icon)}
                        className="task-icon"
                        title={connectors[task.trigger!.connectorId].displayName}
                        alt={connectors[task.trigger!.connectorId].displayName}
                    />
                    {/* Use only unique connectors to show their icon to avoid duplicates */}
                    {/* and breaking the layout */}
                    {uniqueConnectorIdsFromActions(task.actions).map((connectorId) => {
                        const actionConnector = connectors[connectorId];
                        return (
                            <div className="task-action-icons" key={connectorId}>
                                <ExIcon className="icon-plus" icon="plus" hide-browser-tooltip />
                                <img
                                    className="task-icon"
                                    src={base64SvgToSrc(actionConnector.icon)}
                                    title={actionConnector.displayName}
                                    alt={actionConnector.displayName}
                                />
                            </div>
                        );
                    })}
                </div>
                <span className="title lg task-description">
                    <TextWithArrows text={task.displayName} />
                </span>
                <div className="group-row task-act">
                    <strong className="data">
                        <Odometer className="odometer" value={getActivations(task)} format="d" />
                    </strong>
                    <label className="label">{translations[language].TASKS_unit_activations}</label>
                </div>
                <div className="group-row task-save">
                    <strong className="data">
                        <Odometer
                            className="odometer"
                            value={Math.ceil(getCombinedMinutesSaved(task))}
                            format="d"
                        />
                    </strong>
                    <label className="label">{translations[language].TASKS_minutes_saved}</label>
                </div>
                <div className="group-row task-date">
                    <label className="label">{translations[language].TASKS_created}:</label>
                    <span className="data">{getCreationDate(task)}</span>
                </div>
            </AnimatedLink>
            <Toggle
                checked={task.isActive}
                onToggle={() => handleToggle(task.id)}
                disabled={!isTaskAuthenticated(task, connectors) || !isOnline}
            />
            <ExButton
                className="delete-action exo-button-action"
                flavor="risky"
                size="large"
                type="tertiary"
                onClick={() => setDeletingTask(task.id)}
                title={`${translations[language].COMMON_delete} ${task.displayName}`}
            >
                <ExIcon icon="trash" hide-browser-tooltip />
            </ExButton>
            <TaskNotificationBar taskId={task.id} />
        </div>
    );
};

export default TaskItem;
